import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Plus, Pencil, Trash2, ArrowLeft, Save, Loader2, ExternalLink, Youtube, Sparkles, TrendingUp, Upload } from "lucide-react";
import { Input } from "@/components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const NETWORKS = ["VLMAG", "VLMAG MUSIC", "VLMAG STYLE"];
const YT_OPTIONS = ["AUTO", "VLMAG", "VLMAG MUSIC", "VLMAG STYLE"];

const EMPTY = { title: "", network: "VLMAG", category: "", excerpt: "", image: "", author: "VLMAG Editorial", body: "", video_url: "", product_url: "", product_label: "", series: "", part: "", status: "published", pinned: false };

export default function StoriesManager({ token, role }) {
  const isAdmin = (role || "admin") === "admin";
  const headers = { Authorization: `Bearer ${token}` };
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("list"); // list | edit
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [ytVideos, setYtVideos] = useState([]);
  const [ytLoading, setYtLoading] = useState(false);
  const [ytError, setYtError] = useState("");
  const [ytNetwork, setYtNetwork] = useState("AUTO");
  const [ytModel, setYtModel] = useState("claude");
  const [generatingId, setGeneratingId] = useState("");
  const [draftNote, setDraftNote] = useState("");
  const [batchPublish, setBatchPublish] = useState(false);
  const [batch, setBatch] = useState(null); // {status,total,processed,created,skipped,errors}
  const [uploadingCover, setUploadingCover] = useState(false);

  const uploadCover = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingCover(true); setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/admin/media/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, image: data.path }));
    } catch (err) {
      setError("Cover upload failed. Try a different image.");
    } finally {
      setUploadingCover(false);
      e.target.value = "";
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/stories`, { headers });
      setStories(data);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm({ ...EMPTY, status: isAdmin ? "published" : "draft" }); setEditingId(null); setError(""); setDraftNote(""); setView("edit"); };
  const runBatch = async () => {
    setYtError(""); setBatch({ status: "running", total: 0, processed: 0, created: 0, skipped: 0, errors: 0 });
    try {
      const { data } = await axios.post(`${API}/admin/youtube/batch`, { network: ytNetwork, publish: batchPublish, model: ytModel }, { headers });
      const jobId = data.job_id;
      const poll = setInterval(async () => {
        try {
          const { data: st } = await axios.get(`${API}/admin/youtube/batch/${jobId}`, { headers });
          setBatch(st);
          if (st.status === "done" || st.status === "error") {
            clearInterval(poll);
            await load();
            if (st.status === "error") setYtError(st.error || "Batch failed.");
          }
        } catch { clearInterval(poll); }
      }, 2000);
    } catch (err) {
      setBatch(null);
      setYtError("Couldn't start batch import. Try again.");
    }
  };

  const openEdit = (s) => {
    setForm({
      title: s.title, network: s.network, category: s.category, excerpt: s.excerpt,
      image: s.image, author: s.author, body: (s.content || []).join("\n\n"),
      video_url: s.video_url || "", product_url: s.product_url || "", product_label: s.product_label || "", series: s.series || "", part: s.part || "",
      status: s.status || (s.published !== false ? "published" : "draft"),
      pinned: !!s.pinned,
    });
    setEditingId(s.id); setError(""); setDraftNote(""); setView("edit");
  };

  const openYoutube = async () => {
    setView("youtube"); setYtError(""); setYtLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/youtube/videos`, { headers });
      setYtVideos(data.videos || []);
    } catch (err) {
      setYtError("Couldn't load YouTube videos. Try again.");
    } finally {
      setYtLoading(false);
    }
  };

  const generateFromVideo = async (v) => {
    setYtError(""); setGeneratingId(v.video_id);
    try {
      const { data } = await axios.post(`${API}/admin/youtube/draft`,
        { video_id: v.video_id, title: v.title, description: v.description, network: ytNetwork, model: ytModel },
        { headers });
      setForm({
        title: data.title, network: data.network, category: data.category, excerpt: data.excerpt,
        image: data.image, author: "VLMAG Editorial", body: (data.content || []).join("\n\n"),
        video_url: data.video_url, product_url: "", product_label: "", series: "", part: "", status: "draft",
      });
      setEditingId(null); setError(""); setView("edit");
      setDraftNote(data.transcript_used
        ? "AI draft generated from the video transcript — review and publish."
        : "AI draft generated from the video title & description (no captions found) — review and publish.");
    } catch (err) {
      const d = err?.response?.data?.detail;
      setYtError(typeof d === "string" ? d : "Draft generation failed. Try again.");
    } finally {
      setGeneratingId("");
    }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setError("");
    if (!form.title.trim() || !form.category.trim() || !form.excerpt.trim() || !form.image.trim()) {
      setError("Title, category, excerpt and cover image are required."); return;
    }
    const payload = {
      title: form.title, network: form.network, category: form.category, excerpt: form.excerpt,
      image: form.image, author: form.author || "VLMAG Editorial",
      content: form.body.split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean),
      video_url: form.video_url || "",
      product_url: form.product_url || "",
      product_label: form.product_label || "",
      series: form.series || "", part: form.part ? Number(form.part) : 0,
      status: form.status,
      published: form.status === "published",
      pinned: !!form.pinned,
    };
    setSaving(true);
    try {
      if (editingId) await axios.put(`${API}/stories/${editingId}`, payload, { headers });
      else await axios.post(`${API}/stories`, payload, { headers });
      await load();
      setView("list");
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === "string" ? d : "Save failed. Try again.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete "${s.title}"? This cannot be undone.`)) return;
    await axios.delete(`${API}/stories/${s.id}`, { headers });
    await load();
  };

  if (view === "edit") {
    const inputCls = "h-12 rounded-none bg-black/40 border-white/25 focus-visible:border-[#E30613] focus-visible:ring-0 focus-visible:ring-offset-0 text-white placeholder:text-neutral-600";
    return (
      <div className="max-w-3xl" data-testid="story-editor">
        <button onClick={() => setView("list")} data-testid="editor-back" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold text-neutral-400 hover:text-white transition-colors mb-6">
          <ArrowLeft size={16} /> Back to articles
        </button>
        <h2 className="font-display uppercase text-3xl tracking-tight mb-6">{editingId ? "Edit Article" : "New Article"}</h2>
        {draftNote && (
          <div data-testid="draft-note" className="flex items-center gap-2 border border-[#E30613]/40 bg-[#E30613]/10 text-neutral-200 text-xs px-4 py-3 mb-6">
            <Sparkles size={14} className="text-[#E30613]" /> {draftNote}
          </div>
        )}
        <div className="space-y-4">
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-2">Title</p>
            <Input data-testid="story-title" value={form.title} onChange={set("title")} placeholder="Headline" className={inputCls} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-2">Network</p>
              <div className="flex flex-wrap gap-2" data-testid="story-networks">
                {NETWORKS.map((n) => (
                  <button key={n} type="button" data-testid={`story-network-${n.replace(/\s/g, "-").toLowerCase()}`}
                    onClick={() => setForm((f) => ({ ...f, network: n }))}
                    className={`px-3 py-2 text-[10px] uppercase tracking-[0.15em] font-bold border transition-colors ${form.network === n ? "bg-[#E30613] border-[#E30613] text-white" : "border-white/25 text-neutral-400 hover:text-white"}`}>
                    {n}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-2">Category</p>
              <Input data-testid="story-category" value={form.category} onChange={set("category")} placeholder="e.g. Interview, Editorial" className={inputCls} />
            </div>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-2">Author</p>
            <Input data-testid="story-author" value={form.author} onChange={set("author")} placeholder="VLMAG Editorial" className={inputCls} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="sm:col-span-2">
              <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-2">Series (optional — recurring badge, e.g. VLMAG Music Exclusive)</p>
              <Input data-testid="story-series" value={form.series} onChange={set("series")} placeholder="VLMAG Music Exclusive" className={inputCls} />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-2">Part #</p>
              <Input data-testid="story-part" type="number" min="1" value={form.part} onChange={set("part")} placeholder="1" className={inputCls} />
            </div>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-2">Excerpt / standfirst</p>
            <textarea data-testid="story-excerpt" value={form.excerpt} onChange={set("excerpt")} rows={2} placeholder="One-line summary shown on cards and at the top of the article." className="w-full rounded-none bg-black/40 border border-white/25 focus:border-[#E30613] focus:outline-none text-white placeholder:text-neutral-600 p-3 text-sm" />
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-2">Cover image</p>
            <div className="flex gap-2">
              <Input data-testid="story-image" value={form.image} onChange={set("image")} placeholder="https://... or upload" className={inputCls} />
              <label data-testid="story-image-upload" className="cursor-pointer shrink-0 inline-flex items-center gap-2 px-4 text-[10px] uppercase tracking-[0.2em] font-bold border border-white/25 text-neutral-300 hover:text-white hover:border-white/60 transition-colors">
                {uploadingCover ? <Loader2 className="animate-spin" size={14} /> : <Upload size={14} />} {uploadingCover ? "…" : "Upload"}
                <input type="file" accept="image/*" className="hidden" onChange={uploadCover} />
              </label>
            </div>
            {form.image && <img src={form.image.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${form.image}` : form.image} alt="preview" data-testid="story-image-preview" className="mt-3 w-full max-w-sm aspect-[4/3] object-cover grayscale-img border border-white/10" onError={(e) => { e.target.style.display = "none"; }} onLoad={(e) => { e.target.style.display = "block"; }} />}
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-2">YouTube video URL (optional — embeds a player in the article)</p>
            <Input data-testid="story-video" value={form.video_url} onChange={set("video_url")} placeholder="https://www.youtube.com/watch?v=..." className={inputCls} />
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-2">Shop / product URL (optional — adds a Shop button to the Feature card and article)</p>
            <Input data-testid="story-product" value={form.product_url} onChange={set("product_url")} placeholder="https://vlmagstore.com/products/..." className={inputCls} />
            {form.product_url && (
              <Input data-testid="story-product-label" value={form.product_label} onChange={set("product_label")} placeholder='Button text, e.g. "Shop CREATOR TAG" (default: Shop The Drop)' className={`${inputCls} mt-2`} />
            )}
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-2">Body (separate paragraphs with a blank line)</p>
            <textarea data-testid="story-body" value={form.body} onChange={set("body")} rows={10} placeholder={"First paragraph...\n\nSecond paragraph..."} className="w-full rounded-none bg-black/40 border border-white/25 focus:border-[#E30613] focus:outline-none text-white placeholder:text-neutral-600 p-4 text-sm leading-relaxed" />
          </div>
          <div data-testid="story-status">
            <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-2">Status</p>
            <div className="flex gap-2">
              {[["draft", "Draft"], ["review", "Review"], ["published", "Published"]].map(([k, l]) => {
                const locked = k === "published" && !isAdmin;
                return (
                  <button key={k} type="button" data-testid={`status-${k}`} disabled={locked}
                    onClick={() => !locked && setForm((f) => ({ ...f, status: k }))}
                    title={locked ? "Only admins can publish" : ""}
                    className={`px-4 py-2.5 text-[10px] uppercase tracking-[0.2em] font-bold border transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${form.status === k ? "bg-[#E30613] border-[#E30613] text-white" : "border-white/25 text-neutral-400 hover:text-white"}`}>
                    {l}
                  </button>
                );
              })}
            </div>
            <p className="text-[10px] text-neutral-600 mt-2">{!isAdmin ? "Editors can save Draft or Review. Only admins can publish." : form.status === "published" ? "Live on the public site." : form.status === "review" ? "Held for editorial review — hidden from the site." : "Draft — hidden from the site."}</p>
          </div>

          <div data-testid="story-pinned">
            <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-2">Top Feature</p>
            <button type="button" data-testid="pin-toggle" onClick={() => setForm((f) => ({ ...f, pinned: !f.pinned }))}
              className={`px-4 py-2.5 text-[10px] uppercase tracking-[0.2em] font-bold border transition-colors ${form.pinned ? "bg-white border-white text-black" : "border-white/25 text-neutral-400 hover:text-white"}`}>
              {form.pinned ? "Pinned to homepage top" : "Pin to homepage top"}
            </button>
            <p className="text-[10px] text-neutral-600 mt-2">{form.pinned ? "This story headlines the homepage Feature until unpinned." : "Otherwise the Feature shows the most-shared or newest story."}</p>
          </div>

          {error && <p data-testid="story-error" className="text-[#E30613] text-sm">{error}</p>}

          <div className="pt-2">
            <button onClick={save} data-testid="story-save" disabled={saving} className="inline-flex items-center gap-2 px-6 h-12 text-[10px] uppercase tracking-[0.2em] font-bold bg-[#E30613] text-white hover:bg-[#c00510] transition-colors disabled:opacity-60">
              {saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />} {editingId ? "Update Article" : "Publish Article"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (view === "youtube") {
    return (
      <div data-testid="youtube-import">
        <button onClick={() => setView("list")} data-testid="youtube-back" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold text-neutral-400 hover:text-white transition-colors mb-6">
          <ArrowLeft size={16} /> Back to articles
        </button>
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
          <div>
            <h2 className="font-display uppercase text-3xl tracking-tight">Import from YouTube</h2>
            <p className="text-neutral-400 text-sm mt-2">Latest videos from @VLMAGONLINE. Pick one — AI writes an editorial draft you can review before publishing.</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-2">Assign to network</p>
            <div className="flex flex-wrap gap-2" data-testid="youtube-network">
              {YT_OPTIONS.map((n) => (
                <button key={n} type="button" data-testid={`youtube-network-${n.replace(/\s/g, "-").toLowerCase()}`}
                  onClick={() => setYtNetwork(n)}
                  className={`px-3 py-2 text-[10px] uppercase tracking-[0.15em] font-bold border transition-colors ${ytNetwork === n ? "bg-[#E30613] border-[#E30613] text-white" : "border-white/25 text-neutral-400 hover:text-white"}`}>
                  {n === "AUTO" ? "AI Auto-Sort" : n}
                </button>
              ))}
            </div>
            <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-2 mt-4">AI writer</p>
            <div className="flex flex-wrap gap-2" data-testid="youtube-model">
              {[{ id: "claude", label: "Claude" }, { id: "gpt", label: "ChatGPT (GPT-5.5)" }].map((m) => (
                <button key={m.id} type="button" data-testid={`youtube-model-${m.id}`}
                  onClick={() => setYtModel(m.id)}
                  className={`px-3 py-2 text-[10px] uppercase tracking-[0.15em] font-bold border transition-colors ${ytModel === m.id ? "bg-[#E30613] border-[#E30613] text-white" : "border-white/25 text-neutral-400 hover:text-white"}`}>
                  {m.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Batch import */}
        <div className="border border-white/10 p-4 mb-6 flex flex-col sm:flex-row sm:items-center gap-4" data-testid="youtube-batch-bar">
          <div className="flex-1">
            <p className="text-sm text-white font-bold">One-click content fill</p>
            <p className="text-xs text-neutral-500 mt-1">Generate articles for every new video at once (already-imported videos are skipped).</p>
          </div>
          <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="batch-publish-toggle">
            <input type="checkbox" checked={batchPublish} onChange={(e) => setBatchPublish(e.target.checked)} className="w-4 h-4 accent-[#E30613]" />
            <span className="text-[10px] uppercase tracking-[0.15em] font-bold text-neutral-300">{batchPublish ? "Publish live" : "Save as drafts"}</span>
          </label>
          <button
            onClick={runBatch} disabled={batch?.status === "running"}
            data-testid="youtube-batch-run"
            className="inline-flex items-center justify-center gap-2 px-5 h-11 text-[10px] uppercase tracking-[0.2em] font-bold bg-[#E30613] text-white hover:bg-[#c00510] transition-colors disabled:opacity-60">
            {batch?.status === "running" ? <><Loader2 className="animate-spin" size={14} /> Writing…</> : <><Sparkles size={14} /> Generate All</>}
          </button>
        </div>
        {batch && (
          <div className="mb-6" data-testid="batch-progress">
            <div className="flex items-center justify-between text-xs text-neutral-400 mb-2">
              <span>{batch.status === "done" ? "Done" : batch.status === "error" ? "Failed" : "Generating articles…"}</span>
              <span>{batch.processed}/{batch.total || "…"} · {batch.created} created{batch.skipped ? ` · ${batch.skipped} skipped` : ""}{batch.errors ? ` · ${batch.errors} failed` : ""}</span>
            </div>
            <div className="h-1.5 bg-white/10 overflow-hidden">
              <div className="h-full bg-[#E30613] transition-all duration-500" style={{ width: `${batch.total ? (batch.processed / batch.total) * 100 : (batch.status === "done" ? 100 : 8)}%` }} />
            </div>
          </div>
        )}
        {ytError && <p data-testid="youtube-error" className="text-[#E30613] text-sm mb-4">{ytError}</p>}
        {ytLoading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="animate-spin text-[#E30613]" size={28} /></div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="youtube-videos">
            {ytVideos.map((v) => (
              <div key={v.video_id} className="border border-white/10 flex flex-col" data-testid={`youtube-video-${v.video_id}`}>
                <div className="relative aspect-video overflow-hidden bg-neutral-900">
                  <img src={v.thumbnail} alt={v.title} className="w-full h-full object-cover" />
                </div>
                <div className="p-4 flex flex-col gap-3 flex-1">
                  <p className="text-white text-sm font-bold leading-snug flex-1">{v.title}</p>
                  <button
                    onClick={() => generateFromVideo(v)} disabled={!!generatingId}
                    data-testid={`youtube-generate-${v.video_id}`}
                    className="inline-flex items-center justify-center gap-2 px-4 h-10 text-[10px] uppercase tracking-[0.2em] font-bold bg-[#E30613] text-white hover:bg-[#c00510] transition-colors disabled:opacity-60">
                    {generatingId === v.video_id ? <><Loader2 className="animate-spin" size={14} /> Writing…</> : <><Sparkles size={14} /> Generate Draft</>}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div data-testid="stories-manager">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <p className="text-neutral-400 text-sm">{stories.length} article(s). Published stories appear on the live site.</p>
        <div className="flex gap-2">
          <button onClick={openYoutube} data-testid="story-import-youtube" className="inline-flex items-center gap-2 px-5 h-11 text-[10px] uppercase tracking-[0.2em] font-bold border border-white/25 text-neutral-300 hover:text-white hover:border-white/60 transition-colors">
            <Youtube size={15} /> Import from YouTube
          </button>
          <button onClick={openNew} data-testid="story-new" className="inline-flex items-center gap-2 px-5 h-11 text-[10px] uppercase tracking-[0.2em] font-bold bg-white text-black hover:bg-neutral-200 transition-colors">
            <Plus size={15} /> New Article
          </button>
        </div>
      </div>
      {loading ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="animate-spin text-[#E30613]" size={28} /></div>
      ) : (
        <>
        {(() => {
          const ranked = [...stories].filter((s) => (s.share_clicks || 0) > 0).sort((a, b) => (b.share_clicks || 0) - (a.share_clicks || 0)).slice(0, 5);
          if (ranked.length === 0) return null;
          const max = ranked[0].share_clicks || 1;
          return (
            <div className="border border-[#E30613]/40 bg-gradient-to-br from-[#E30613]/10 via-black to-black p-6 md:p-7 mb-6" data-testid="share-leaderboard">
              <p className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] font-bold text-[#E30613] mb-5">
                <TrendingUp size={14} /> Most Shared — Gaining Traction
              </p>
              <div className="space-y-3">
                {ranked.map((s, i) => (
                  <div key={s.id} className="flex items-center gap-4" data-testid={`leaderboard-row-${i}`}>
                    <span className="font-display text-2xl text-neutral-600 w-7 shrink-0">{i + 1}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-4">
                        <p className="text-white text-sm font-bold truncate">{s.title}</p>
                        <span className="text-[#E30613] text-sm font-bold tabular-nums shrink-0" data-testid={`leaderboard-count-${i}`}>{s.share_clicks} shares</span>
                      </div>
                      <div className="mt-1.5 h-1.5 bg-white/10 overflow-hidden">
                        <div className="h-full bg-[#E30613]" style={{ width: `${Math.max(6, ((s.share_clicks || 0) / max) * 100)}%` }} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })()}
        <div className="border border-white/10" data-testid="stories-table">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-white/10">
                {["Title", "Network", "Category", "Status", "Shares", ""].map((h, i) => (
                  <th key={i} className="px-4 py-3 text-[10px] uppercase tracking-[0.18em] font-bold text-neutral-500">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {stories.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-neutral-600">No articles yet. Create your first.</td></tr>
              ) : stories.map((s, i) => (
                <tr key={s.id} className="border-b border-white/5 hover:bg-white/5" data-testid={`story-row-${i}`}>
                  <td className="px-4 py-3 text-white max-w-xs">{s.title}</td>
                  <td className="px-4 py-3 text-neutral-400 whitespace-nowrap">{s.network}</td>
                  <td className="px-4 py-3 text-neutral-400 whitespace-nowrap">{s.category}</td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className={`text-[10px] uppercase tracking-[0.15em] font-bold ${s.published !== false ? "text-[#E30613]" : "text-neutral-600"}`}>{s.published !== false ? "Live" : "Draft"}</span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-neutral-300 font-bold tabular-nums" data-testid={`story-shares-${i}`}>{s.share_clicks || 0}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-right">
                    <div className="inline-flex items-center gap-3">
                      <a href={`/story/${s.id}`} target="_blank" rel="noopener noreferrer" title="Preview" className="text-neutral-500 hover:text-white transition-colors"><ExternalLink size={16} /></a>
                      <button onClick={() => openEdit(s)} data-testid={`story-edit-${i}`} title="Edit" className="text-neutral-500 hover:text-white transition-colors"><Pencil size={16} /></button>
                      <button onClick={() => remove(s)} data-testid={`story-delete-${i}`} title="Delete" className="text-neutral-500 hover:text-[#E30613] transition-colors"><Trash2 size={16} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </>
      )}
    </div>
  );
}
